import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { MediaSummary } from "@media-viewer/shared";
import { dropIdsFromMediaListCaches } from "../util/media-cache";
import { TypeBadge } from "./TypeBadge";
import { ContextMenu, type ContextMenuItem } from "./ContextMenu";
import { TagModal } from "./TagModal";
import { AddToPlaylistModal } from "./AddToPlaylistModal";
import { useSelection } from "./SelectionContext";
import { api } from "../api/client";
import { getLightboxPref } from "../util/lightboxPref";
import { buildDetailHref } from "../util/detail-url";

interface MediaTileProps {
  item: MediaSummary;
  /** The set descriptor passed to the detail link as ?set=. */
  setParam: string;
  /** True when admin features (delete) should be exposed. */
  isAdmin: boolean;
  /** Whether item is currently favorited (from the list payload, optional). */
  favorited?: boolean;
  /** Index of this tile in the visible list — used for shift-range selection. */
  index?: number;
  /** Aspect ratio override; defaults to natural width/height or square. */
  aspect?: string;
  /** Disable the right-click context menu. */
  disableContextMenu?: boolean;
  /** Render lazy `loading="lazy"` on the img. Default true. */
  lazy?: boolean;
  /** Optional extra menu items appended to the bottom of the right-click menu. */
  extraMenuItems?: ContextMenuItem[];
  /** Extra query params to carry into the detail link (e.g., active duration filter). */
  extraDetailParams?: Record<string, string>;
}

export function MediaTile({
  item,
  setParam,
  isAdmin,
  favorited,
  index,
  aspect,
  disableContextMenu,
  lazy = true,
  extraMenuItems,
  extraDetailParams,
}: MediaTileProps) {
  const sel = useSelection();
  const qc = useQueryClient();
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [tagOpen, setTagOpen] = useState(false);
  const [plOpen, setPlOpen] = useState(false);
  // The thumb endpoint 404s while a freshly-ingested item's thumbnail is
  // still being generated. Show a "processing" placeholder instead of a
  // broken-image icon. (Per-tile state; tiles are keyed by id in the grid,
  // so this resets naturally when the tile is reused for a new item.)
  const [thumbFailed, setThumbFailed] = useState(false);

  const isSelected = sel.isSelected(item.id);
  const isFavorited = favorited;

  const toggleFav = useMutation({
    mutationFn: ({ on }: { on: boolean }) => api.toggleFavorite(item.id, on),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["favorites"] });
    },
  });

  const del = useMutation({
    mutationFn: () => api.deleteMedia(item.id),
    onSuccess: () => {
      // Optimistic: drop the deleted id from every cached infinite-list
      // entry so the grid updates instantly. Invalidate remains as the
      // eventual-consistency backstop for count queries, other observers,
      // and anything that wasn't in the optimistic path.
      dropIdsFromMediaListCaches(qc, [item.id]);
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["favorites"] });
      qc.invalidateQueries({ queryKey: ["playlists"] });
    },
  });

  function onClick(e: React.MouseEvent): void {
    if (sel.active) {
      e.preventDefault();
      sel.toggle(item.id, { index, shift: e.shiftKey });
    }
  }

  function onContextMenu(e: React.MouseEvent): void {
    if (disableContextMenu) return;
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY });
  }

  const detailHref = buildDetailHref({
    id: item.id,
    setParam,
    lightbox: getLightboxPref(),
    extra: extraDetailParams,
  });

  const menuItems: ContextMenuItem[] = [
    {
      label: "Open in new tab",
      onClick: () => {
        window.open(detailHref, "_blank", "noopener,noreferrer");
      },
    },
    {
      label: isFavorited ? "♥ Unfavorite" : "♡ Favorite",
      onClick: () => toggleFav.mutate({ on: !isFavorited }),
    },
    { label: "🏷 Tag…", onClick: () => setTagOpen(true) },
    { label: "📂 Add to playlist…", onClick: () => setPlOpen(true) },
    ...(extraMenuItems ?? []),
    ...(isAdmin
      ? [
          {
            label: "🗑 Delete",
            danger: true,
            divider: true,
            hint: "admin",
            onClick: () => {
              if (window.confirm(`Delete ${item.relativePath}?\n\nThe file will be moved to /media/.media-viewer/trash/.`)) {
                del.mutate();
              }
            },
          } satisfies ContextMenuItem,
        ]
      : []),
  ];

  const ar = aspect ?? (item.width && item.height ? `${item.width} / ${item.height}` : undefined);

  return (
    <>
      <a
        href={detailHref}
        onClick={onClick}
        onContextMenu={onContextMenu}
        className={
          "group relative mb-3 block break-inside-avoid overflow-hidden rounded transition-shadow " +
          (isSelected
            ? "outline outline-2 outline-emerald-400 ring-2 ring-emerald-400/40"
            : "bg-zinc-900")
        }
        style={ar ? { aspectRatio: ar } : { aspectRatio: "1 / 1" }}
      >
        {thumbFailed ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-zinc-800 text-zinc-500"
            aria-label="Thumbnail still processing"
          >
            <svg className="h-7 w-7 animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            <span className="text-[10px]">processing…</span>
          </div>
        ) : (
          <img
            src={`${item.thumbUrl}?size=512`}
            alt=""
            loading={lazy ? "lazy" : undefined}
            decoding="async"
            onError={() => setThumbFailed(true)}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
          />
        )}
        <TypeBadge kind={item.kind} isAnimated={item.isAnimated} />
        {sel.active && (
          <div
            className={
              "absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded border " +
              (isSelected
                ? "border-emerald-300 bg-emerald-500 text-white"
                : "border-zinc-300 bg-black/60 text-transparent")
            }
            aria-hidden
          >
            ✓
          </div>
        )}
        {item.kind === "video" && item.durationMs != null && (
          <div className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {formatDuration(item.durationMs)}
          </div>
        )}
        {isFavorited && !sel.active && (
          <div className="absolute bottom-1 left-1 text-rose-400" aria-hidden>
            ♥
          </div>
        )}
      </a>

      {menu && <ContextMenu x={menu.x} y={menu.y} items={menuItems} onClose={() => setMenu(null)} />}
      <TagModal open={tagOpen} onClose={() => setTagOpen(false)} mediaIds={[item.id]} />
      <AddToPlaylistModal open={plOpen} onClose={() => setPlOpen(false)} mediaIds={[item.id]} />
    </>
  );
}

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}
